import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { CheckCircle2, Send } from 'lucide-react';
import { Button } from '../../components/Button';
import { submitB2BLead, track } from '../../api/client';
import { telegram } from '../../telegram/bridge';
import { useI18n } from '../../i18n/I18nProvider';
import { LEGAL_VERSIONS } from '../../../shared/legal';

export function B2BForm({ sessionId, username, source, onSuccess }: { sessionId: string; username: string; source: string; onSuccess: () => void }) {
  const { t, locale } = useI18n();
  const [studioName, setStudioName] = useState('');
  const [website, setWebsite] = useState('');
  const [comment, setComment] = useState('');
  const [contact, setContact] = useState('');
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => { track('b2b_form_opened', sessionId); }, [sessionId]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!consent || busy) return;
    if (studioName.trim().length < 2) { setError(t('b2b.nameError')); return; }
    if (!username && contact.trim().length < 3) { setError(t('b2b.contactError')); return; }
    setBusy(true);
    setError('');
    track('b2b_lead_started', sessionId);
    try {
      await submitB2BLead({
        sessionId, studioName: studioName.trim(), website: website.trim(), comment: comment.trim(), contact: contact.trim(), source,
        consentAccepted: true, consentVersion: LEGAL_VERSIONS.consentVersion, privacyVersion: LEGAL_VERSIONS.privacyVersion,
        termsVersion: LEGAL_VERSIONS.termsVersion, locale,
      });
      track('b2b_lead_submitted', sessionId);
      telegram.haptic('success');
      onSuccess();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('b2b.sendError'));
      telegram.haptic('error');
    } finally { setBusy(false); }
  };

  return (
    <form className="b2b-form" onSubmit={(event) => void submit(event)}>
      <div className="b2b-form__heading"><CheckCircle2 /><div><h2>{t('b2b.build')}</h2></div></div>
      <label htmlFor="studio-name"><span>{t('b2b.studioName')}</span><input type="text" id="studio-name" name="studioName" required minLength={2} autoComplete="organization" value={studioName} onChange={(event) => setStudioName(event.target.value)} placeholder={t('b2b.studioPlaceholder')} /></label>
      <label htmlFor="studio-link"><span>{t('b2b.website')}</span><input type="text" id="studio-link" name="website" inputMode="url" autoComplete="url" value={website} onChange={(event) => setWebsite(event.target.value)} placeholder={t('b2b.websitePlaceholder')} /></label>
      <label htmlFor="studio-comment"><span>{t('b2b.messageField')}</span><textarea id="studio-comment" name="comment" rows={3} maxLength={1000} value={comment} onChange={(event) => setComment(event.target.value)} placeholder={t('b2b.messagePlaceholder')} /></label>
      {!username && <label htmlFor="studio-contact"><span>{t('b2b.contact')}</span><input type="text" id="studio-contact" name="contact" required minLength={3} value={contact} onChange={(event) => setContact(event.target.value)} placeholder={t('b2b.contactPlaceholder')} /></label>}
      {error && <p className="inline-error" role="alert">{error}</p>}
      <Button block type="submit" busy={busy} disabled={!consent} icon={<Send />}>{t('b2b.send')}</Button>
      <div className="b2b-consent"><label className="b2b-consent__check"><input type="checkbox" required checked={consent} onChange={(event) => setConsent(event.target.checked)} /><span>{t('b2b.consentCopy')} <a href="/legal/personal-data-consent" target="_blank" rel="noreferrer">{t('b2b.consentLink')}</a>.</span></label><a href="/legal/privacy" target="_blank" rel="noreferrer">{t('legal.privacy')}</a></div>
      <p className="form-privacy">{t('b2b.identity')}</p>
    </form>
  );
}
